import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Box, Button, MenuItem, Stack, TextField } from '@mui/material';
import { useForm, type SubmitHandler } from 'react-hook-form';

import { isApiError } from '@/lib/apiError';
import { CANAL_ENVIO_LABELS, TIPO_PROFESIONAL_LABELS } from '@/utils/labels';
import { CanalEnvio, TipoProfesional } from '@/types/enums';

import { profesionalFormSchema, type ProfesionalFormValues } from '../schemas';
import type { Profesional } from '../types';

import { EmailsField } from './EmailsField';
import { PhoneField } from './PhoneField';

interface ProfesionalFormProps {
  defaultValues?: Profesional;
  onSubmit: SubmitHandler<ProfesionalFormValues>;
  onCancel: () => void;
  submitLabel: string;
  submitting: boolean;
  error?: unknown;
}

const EMPTY_VALUES: ProfesionalFormValues = {
  nombre_apellido: '',
  tipo_profesional: TipoProfesional.VETERINARIO,
  tipo_profesional_otro: '',
  telefono_whatsapp: '',
  emails: [],
  localidad: '',
  envio_informe: CanalEnvio.EMAIL,
};

function profesionalToFormValues(p: Profesional): ProfesionalFormValues {
  return {
    nombre_apellido: p.nombre_apellido,
    tipo_profesional: p.tipo_profesional,
    tipo_profesional_otro: p.tipo_profesional_otro ?? '',
    telefono_whatsapp: p.telefono_whatsapp ?? '',
    emails: p.emails,
    localidad: p.localidad ?? '',
    envio_informe: p.envio_informe,
  };
}

function buildErrorMessage(error: unknown): string | null {
  if (!error) return null;
  if (isApiError(error) && error.isValidationError()) {
    return error.validationErrors().map((e) => `${e.field}: ${e.message}`).join('. ');
  }
  if (isApiError(error)) return error.message;
  if (error instanceof Error) return error.message;
  return null;
}

export function ProfesionalForm({
  defaultValues,
  onSubmit,
  onCancel,
  submitLabel,
  submitting,
  error,
}: ProfesionalFormProps): JSX.Element {
  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors, isDirty },
  } = useForm<ProfesionalFormValues>({
    resolver: zodResolver(profesionalFormSchema),
    defaultValues: defaultValues ? profesionalToFormValues(defaultValues) : EMPTY_VALUES,
  });

  const tipoProfesional = watch('tipo_profesional');
  const requiereTipoOtro = tipoProfesional === TipoProfesional.OTRO;

  const errorMessage = buildErrorMessage(error);

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack spacing={3}>
        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

        <TextField
          label="Nombre y apellido"
          fullWidth
          required
          autoFocus
          {...register('nombre_apellido')}
          error={!!errors.nombre_apellido}
          helperText={errors.nombre_apellido?.message ?? ' '}
        />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            select
            label="Tipo profesional"
            fullWidth
            required
            {...register('tipo_profesional')}
            defaultValue={defaultValues?.tipo_profesional ?? TipoProfesional.VETERINARIO}
            error={!!errors.tipo_profesional}
            helperText={errors.tipo_profesional?.message ?? ' '}
          >
            {Object.values(TipoProfesional).map((value) => (
              <MenuItem key={value} value={value}>
                {TIPO_PROFESIONAL_LABELS[value]}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label='Especificar "otro"'
            fullWidth
            disabled={!requiereTipoOtro}
            {...register('tipo_profesional_otro')}
            error={!!errors.tipo_profesional_otro}
            helperText={
              errors.tipo_profesional_otro?.message ?? (requiereTipoOtro ? 'Requerido' : ' ')
            }
          />
        </Stack>

        <TextField
          label="Localidad"
          fullWidth
          {...register('localidad')}
          error={!!errors.localidad}
          helperText={errors.localidad?.message ?? ' '}
        />

        <PhoneField control={control} />

        <EmailsField control={control} />

        <TextField
          select
          label="Canal de envío de informes"
          fullWidth
          required
          {...register('envio_informe')}
          defaultValue={defaultValues?.envio_informe ?? CanalEnvio.EMAIL}
          error={!!errors.envio_informe}
          helperText={errors.envio_informe?.message ?? ' '}
        >
          {Object.values(CanalEnvio).map((value) => (
            <MenuItem key={value} value={value}>
              {CANAL_ENVIO_LABELS[value]}
            </MenuItem>
          ))}
        </TextField>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
          <Button onClick={onCancel} disabled={submitting}>
            Cancelar
          </Button>
          <Button type="submit" variant="contained" disabled={submitting || !isDirty}>
            {submitting ? 'Guardando...' : submitLabel}
          </Button>
        </Box>
      </Stack>
    </form>
  );
}
